import type { UdacityAssessmentAccessLevel, UserExperience } from '~/features/user/models/user-experience';

/**
 * Derives Udacity assessment access from the experience-api response.
 */
export function getUdacityAssessmentAccessLevel(userExperience: UserExperience): UdacityAssessmentAccessLevel {
  return userExperience.skillAssessments.udacityPublicAssessmentAccess.accessLevel;
}

/** Access levels that grant active product use. SUSPENDED and EXPIRED are excluded. */
const ACTIVE_ACCESS_LEVELS: ReadonlyArray<string> = ['ALL_ACCESS', 'DISCOVERY_AND_FLUENCY'];

/**
 * Returns true if the user has an active Workera contract (ALL_ACCESS or DISCOVERY_AND_FLUENCY)
 * with a valid company ID. SUSPENDED and EXPIRED are treated as no access.
 */
export function hasWorkeraAccess(userExperience: UserExperience): boolean {
  const { companyId, accessLevel } = userExperience.skillAssessments.workeraAccess;
  return Boolean(companyId) && ACTIVE_ACCESS_LEVELS.includes(accessLevel);
}

/**
 * Returns true if the user has active Udacity public assessment access
 * (ALL_ACCESS or DISCOVERY_AND_FLUENCY). SUSPENDED and EXPIRED are treated as no access.
 */
export function hasUdacityPublicAssessmentAccess(userExperience: UserExperience): boolean {
  return ACTIVE_ACCESS_LEVELS.includes(getUdacityAssessmentAccessLevel(userExperience));
}

/**
 * Returns true if the user has active access to skill assessment pages via either
 * a Workera contract or a Udacity public assessment contract.
 */
export function hasSkillAssessmentsAccess(userExperience: UserExperience): boolean {
  return hasWorkeraAccess(userExperience) || hasUdacityPublicAssessmentAccess(userExperience);
}
